import { ReactElement, useId } from 'react';
import { useTheme } from 'styled-components';

import { ButtonSizes, FocusRingVariants } from '../Button';
import { CalmPaletteColor, CalmTheme } from '../ThemeProvider';
import { IconWrapper, Wrapper } from './styles';
import { QuestionnaireTileProps } from './types';

function resolveColor(color: string, theme: typeof CalmTheme): string;
function resolveColor(color: undefined, theme: typeof CalmTheme): undefined;
function resolveColor(color: string | undefined, theme: typeof CalmTheme): string | undefined;
function resolveColor(color: string | undefined, theme: typeof CalmTheme): string | undefined {
	if (!color) {
		return undefined;
	}
	if (color in theme.palette) {
		return theme.palette[color as CalmPaletteColor];
	}
	return color;
}

const QuestionnaireTile = ({
	backgroundColor = 'white',
	title,
	Icon,
	onClick,
	className,
	isSelected = false,
	colors = { color1: 'buttonPrimaryBg' },
	contentAlignment = 'left',
	focusRingVariant = FocusRingVariants.Light,
	marginBottom,
	weight,
	role,
	UNSAFE_showFocusRing,
}: QuestionnaireTileProps): ReactElement => {
	const theme = useTheme() as typeof CalmTheme;
	const gradientId = useId();
	const { color1, color2 } = colors;
	const resolvedColor1 = resolveColor(color1, theme);
	const resolvedColor2 = resolveColor(color2, theme);
	const borderColor = resolvedColor2 ? 'transparent' : isSelected ? resolvedColor1 : theme.palette.gray2;

	return (
		<Wrapper
			backgroundColor={backgroundColor}
			$borderColor={borderColor}
			className={className}
			color1={resolvedColor1}
			color2={resolvedColor2}
			contentAlignment={contentAlignment}
			focusRingVariant={focusRingVariant}
			UNSAFE_showFocusRing={UNSAFE_showFocusRing}
			fullWidth
			isSelected={isSelected}
			onPress={onClick}
			role={role}
			size={ButtonSizes.Md}
			$hasIcon={Boolean(Icon)}
			$marginBottom={marginBottom}
			$weight={weight}
			{...(role === 'checkbox' || role === 'radio' ? { 'aria-checked': isSelected } : {})}
		>
			{Icon && (
				<IconWrapper
					$isSelected={isSelected}
					$color={resolvedColor2 ? 'white' : 'black'}
					$gradientId={gradientId}
					$iconOnly={false}
					$size={ButtonSizes.Md}
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						xmlnsXlink="http://www.w3.org/1999/xlink"
						style={{ width: 0, height: 0, position: 'absolute' }}
						y1="1"
						y2="1"
					>
						<linearGradient id={gradientId} gradientTransform="rotate(55)">
							<stop offset="0%" stopColor={resolvedColor2 ? resolvedColor1 : '#000000'} stopOpacity="1" />
							<stop offset="100%" stopColor={resolvedColor2 ? resolvedColor2 : '#000000'} stopOpacity="1" />
						</linearGradient>
					</svg>
					<Icon aria-hidden="true" />
				</IconWrapper>
			)}
			<span>{title}</span>
		</Wrapper>
	);
};

export default QuestionnaireTile;
